<script setup lang="ts">
import { ref, onMounted } from 'vue';

/**
 * Modale d'onboarding affichée au PREMIER lancement de l'app.
 * Une fois fermée, ne réapparaît plus (mémorisé en localStorage).
 *
 * Pour la rouvrir manuellement, le bouton « Aide » dans la sidebar
 * appelle `forceOpen()` (exposé).
 */
const LS_KEY_SEEN = 'eurorail-onboarding-seen-v1';

const isOpen = ref(false);
const step = ref(0);

const STEPS = [
  {
    icon: 'ti-map-2',
    title: 'Cliquez sur une ville',
    body: "La carte se recolorie aussitôt selon les temps de trajet réels depuis cette ville — train, bus, ou les deux combinés. Chaque couleur représente une bande temporelle.",
  },
  {
    icon: 'ti-plus',
    title: 'Ajoutez vos étapes',
    body: "Cliquez sur « Ajouter à mon itinéraire » dans la popup d'une ville pour la mettre dans votre voyage. Ou utilisez la recherche dans le panneau latéral.",
  },
  {
    icon: 'ti-adjustments',
    title: 'Réglez vos critères',
    body: "Date de départ, mode (rapide, train, bus ou nuit), distance maximale à afficher, filtre « sans correspondance ». Tout se recalcule en temps réel.",
  },
  {
    icon: 'ti-share',
    title: 'Partagez ou exportez',
    body: "Votre itinéraire est encodé dans l'URL : partagez le lien pour le retrouver sur un autre appareil. Vous pouvez aussi l'exporter en texte.",
  },
];

onMounted(() => {
  try {
    if (!localStorage.getItem(LS_KEY_SEEN)) {
      // Petit délai pour que la page ait fini de charger
      setTimeout(() => (isOpen.value = true), 600);
    }
  } catch {
    /* localStorage indisponible : on n'affiche pas l'onboarding */
  }
});

function next() {
  if (step.value < STEPS.length - 1) {
    step.value++;
  } else {
    close();
  }
}

function prev() {
  if (step.value > 0) step.value--;
}

function close() {
  isOpen.value = false;
  step.value = 0;
  try {
    localStorage.setItem(LS_KEY_SEEN, '1');
  } catch {
    /* ignore */
  }
}

/** Rouvre la modale (appelé depuis le bouton « Aide » de la sidebar) */
function forceOpen() {
  step.value = 0;
  isOpen.value = true;
}

defineExpose({ forceOpen });

function onBackdropClick(e: MouseEvent) {
  if (e.target === e.currentTarget) close();
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200"
      leave-active-class="transition duration-200"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[2500] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
        @click="onBackdropClick"
      >
        <div
          class="relative w-full max-w-md bg-surface rounded-lg shadow-2xl overflow-hidden flex flex-col"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-5 pt-5 pb-3"
          >
            <span class="text-xs font-semibold uppercase tracking-wider text-accent">
              Étape {{ step + 1 }} sur {{ STEPS.length }}
            </span>
            <button
              type="button"
              class="text-text-faint hover:text-text -mt-1 -mr-1 w-8 h-8 flex items-center justify-center"
              aria-label="Passer l'introduction"
              @click="close"
            >
              <i class="ti ti-x" aria-hidden="true"></i>
            </button>
          </div>

          <!-- Body -->
          <div class="px-5 pb-5">
            <div class="flex justify-center mb-5">
              <div class="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center text-accent">
                <i :class="['ti', STEPS[step].icon, 'text-3xl']" aria-hidden="true"></i>
              </div>
            </div>
            <h2 class="font-display text-2xl text-center leading-tight mb-3">
              {{ STEPS[step].title }}
            </h2>
            <p class="text-sm text-text-muted text-center leading-relaxed">
              {{ STEPS[step].body }}
            </p>
          </div>

          <!-- Progression dots -->
          <div class="flex justify-center gap-2 pb-5">
            <button
              v-for="(_, i) in STEPS"
              :key="i"
              type="button"
              :aria-label="`Aller à l'étape ${i + 1}`"
              :class="[
                'w-2 h-2 rounded-full transition-all duration-200',
                i === step ? 'bg-accent w-6' : 'bg-text-faint/30 hover:bg-text-faint/50',
              ]"
              @click="step = i"
            ></button>
          </div>

          <!-- Footer -->
          <footer
            class="flex items-center justify-between gap-3 px-5 py-4 bg-surface-muted/40 border-t border-DEFAULT"
          >
            <button
              type="button"
              class="px-3 py-2 text-sm text-text-muted hover:text-text transition-colors duration-160 disabled:opacity-30 disabled:cursor-not-allowed"
              :disabled="step === 0"
              @click="prev"
            >
              Précédent
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-accent text-white rounded-md hover:bg-accent-hover transition-colors duration-160"
              @click="next"
            >
              {{ step === STEPS.length - 1 ? "C'est parti" : 'Suivant' }}
              <i
                :class="['ti', step === STEPS.length - 1 ? 'ti-check' : 'ti-arrow-right']"
                aria-hidden="true"
              ></i>
            </button>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
